import { useCallback, useMemo, useReducer } from "react";
import {
  USER_CAMPAIGN_KEY,
  USER_CONTENT_KEY,
  USER_MEDIUM_KEY,
  USER_SOURCE_KEY,
} from "@hopper-b2b/utilities";

type UtmParameters =
  | "utm_campaign"
  | "utm_medium"
  | "utm_source"
  | "utm_content";

type UserTrackerState = {
  [USER_CAMPAIGN_KEY]: string | undefined;
  [USER_MEDIUM_KEY]: string | undefined;
  [USER_SOURCE_KEY]: string | undefined;
  [USER_CONTENT_KEY]: string | undefined;
};

export type TrackingPropertiesType = Record<
  keyof UserTrackerState | UtmParameters,
  string | undefined
>;

type UserTrackerAction = {
  payload: Partial<TrackingPropertiesType>;
};

export const ParamToKey = {
  [USER_CAMPAIGN_KEY]: USER_CAMPAIGN_KEY,
  utm_campaign: USER_CAMPAIGN_KEY,
  [USER_MEDIUM_KEY]: USER_MEDIUM_KEY,
  utm_medium: USER_MEDIUM_KEY,
  [USER_SOURCE_KEY]: USER_SOURCE_KEY,
  utm_source: USER_SOURCE_KEY,
  [USER_CONTENT_KEY]: USER_CONTENT_KEY,
  utm_content: USER_CONTENT_KEY,
};

const userTrackersReducer = (
  state: UserTrackerState,
  action: UserTrackerAction
): UserTrackerState => {
  const newState = Object.entries(action.payload).reduce(
    (acc, [key, value]) => {
      acc[ParamToKey[key]] = value;
      return acc;
    },
    structuredClone(state)
  );
  // Check if the two objects are the same by shallow-compare them,
  // and return the old state if they are not different to avoid
  // infinite loop of updating pages.
  // Infinite loop can happen because this state will update the AppTrackingContext
  // which wraps almost everything in the page (see App.tsx).
  if (
    Object.entries(newState).sort().toString() ===
    Object.entries(state).sort().toString()
  ) {
    return state;
  } else {
    return newState;
  }
};

export const useAppTrackingProvider = () => {
  const [state, dispatch] = useReducer(
    userTrackersReducer,
    initialAppTrackingProviderContext.trackers
  );

  const setTrackers = useCallback(
    (trackers: Partial<TrackingPropertiesType>) => {
      Object.entries(trackers).forEach(([key, value]) => {
        if (value != null) {
          sessionStorage.setItem(ParamToKey[key], value);
        }
      });
      dispatch({ payload: trackers });
    },
    [dispatch]
  );

  return useMemo(
    () => ({
      trackers: state,
      setTrackers,
    }),
    [state, setTrackers]
  );
};

export const initialAppTrackingProviderContext = {
  trackers: {
    [USER_SOURCE_KEY]: sessionStorage.getItem(USER_SOURCE_KEY) ?? undefined,
    [USER_CAMPAIGN_KEY]: sessionStorage.getItem(USER_CAMPAIGN_KEY) ?? undefined,
    [USER_MEDIUM_KEY]: sessionStorage.getItem(USER_MEDIUM_KEY) ?? undefined,
    [USER_CONTENT_KEY]: sessionStorage.getItem(USER_CONTENT_KEY) ?? undefined,
  },
  setTrackers: () => {
    throw new Error("AppTrackingProviderContext isn't initialized yet");
  },
};
